import api from '@/shared/api';
import {
  extractApiConflictingIndexes,
  extractApiErrorCode,
  extractApiErrorMessage,
  extractApiFieldErrors,
  type ApiResponse,
} from '@/shared/lib';
import { SettingsNameConflictError } from '../lib/settings-name-conflict';
import { SettingsValidationError } from '../lib/settings-validation-error';
import type { SettingsListQuery } from '../lib/settings-list-query';
import type {
  SettingsListItem,
  SettingsListItemCreated,
  SettingsListItemUpdated,
} from '../model/settings.types';

const NAME_ALREADY_EXISTS_CODE = 'RECORD_SETTING_NAME_ALREADY_EXISTS';

/**
 * The five record settings endpoints answer the same envelope and the same rejections, so the
 * translation of a rejection lives here once: a duplicate name has to keep the positions the
 * request sent, and a validation rejection has to keep the messages the server wrote, or the modal
 * shows its own generic text instead of what was refused.
 *
 * Exported because Origem do Registro does not go through this factory: its payload carries the
 * two sets of linked identifiers, so it has its own adapter and reuses only this.
 */
export function translateSettingsApiError(error: unknown): unknown {
  if (extractApiErrorCode(error) === NAME_ALREADY_EXISTS_CODE) {
    return new SettingsNameConflictError({
      message: extractApiErrorMessage(error),
      conflictingIndexes: extractApiConflictingIndexes(error),
    });
  }

  const messages = Object.values(extractApiFieldErrors(error)).flat();
  if (messages.length > 0) return new SettingsValidationError(messages);

  return error;
}

export interface SettingsListHttpApi {
  list(query?: SettingsListQuery): Promise<SettingsListItem[]>;
  create(names: string[]): Promise<SettingsListItemCreated[]>;
  update(id: number, name: string): Promise<SettingsListItemUpdated | null>;
  remove(id: number): Promise<boolean>;
}

export function createSettingsListApi(basePath: string): SettingsListHttpApi {
  return {
    list: async (query: SettingsListQuery = {}): Promise<SettingsListItem[]> => {
      const response = await api.get<ApiResponse<SettingsListItem[]>>(basePath, { params: query });

      return response.data.data ?? [];
    },

    /**
     * One request for the whole batch: that is what makes it transactional on the server and what
     * makes the reported conflicting positions line up with the fields the modal sent.
     */
    create: async (names: string[]): Promise<SettingsListItemCreated[]> => {
      try {
        const response = await api.post<ApiResponse<SettingsListItemCreated[]>>(basePath, {
          names,
        });

        return response.data.data ?? [];
      } catch (error) {
        throw translateSettingsApiError(error);
      }
    },

    update: async (id: number, name: string): Promise<SettingsListItemUpdated | null> => {
      try {
        const response = await api.patch<ApiResponse<SettingsListItemUpdated>>(
          `${basePath}/${id}`,
          { name }
        );

        return response.data.data ?? null;
      } catch (error) {
        throw translateSettingsApiError(error);
      }
    },

    // The API answers 204 with no body, so the caller has to be told the row is gone explicitly.
    remove: async (id: number): Promise<boolean> => {
      await api.delete(`${basePath}/${id}`);

      return true;
    },
  };
}
